import { Alert, Button, Card, DatePicker, Form, Input, Select, message } from 'antd';
import dayjs from 'dayjs';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type FormValues = {
  title: string;
  category: string;
  summary: string;
  body_text?: string;
  effective_from: dayjs.Dayjs;
  effective_to?: dayjs.Dayjs;
};

export default function GovernancePolicyFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.governance.manage') || user?.permissions?.includes('education.admin')
  );

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<{ id: string }>('/education/governance/policies', {
        title: values.title,
        category: values.category,
        summary: values.summary,
        body_text: values.body_text,
        effective_from: values.effective_from.format('YYYY-MM-DD'),
        effective_to: values.effective_to ? values.effective_to.format('YYYY-MM-DD') : undefined,
      }),
    onSuccess: (res) => {
      message.success(t('governancePolicyCreated'));
      const id = res.data?.id;
      navigate(id ? `/education/governance/policies/${id}` : '/education/governance/policies');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('newGovernancePolicy')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('newGovernancePolicy')}
      description={t('governancePoliciesIntro')}
      extra={<Button onClick={() => navigate('/education/governance/policies')}>{t('governancePolicies')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('governanceSeparateHint')} />
        <Form
          layout="vertical"
          initialValues={{ effective_from: dayjs(), category: 'academic_ops' }}
          onFinish={(values) => create.mutate(values)}
        >
          <Form.Item name="title" label={t('title')} rules={[{ required: true }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="category" label={t('category')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'safeguarding_ops', label: t('govCatSafeguarding') },
                { value: 'attendance_ops', label: t('govCatAttendance') },
                { value: 'academic_ops', label: t('govCatAcademic') },
                { value: 'fee_ops', label: t('govCatFee') },
                { value: 'hr_ops', label: t('govCatHr') },
                { value: 'facilities', label: t('govCatFacilities') },
                { value: 'other', label: t('govCatOther') },
              ]}
            />
          </Form.Item>
          <Form.Item name="effective_from" label={t('effectiveFrom')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
          </Form.Item>
          <Form.Item name="effective_to" label={t('effectiveTo')}>
            <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
          </Form.Item>
          <Form.Item name="summary" label={t('summary')} rules={[{ required: true }]}>
            <Input maxLength={500} />
          </Form.Item>
          <Form.Item name="body_text" label={t('policyBody')}>
            <Input.TextArea rows={6} maxLength={4000} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending}>
            {t('save')}
          </Button>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
